// src/pages/Dashboard/RecentActivityWidget.jsx
//
// Timeline of the most recent activity across three channels the user
// actually cares about: alarms, tickets, and notifications. Runs three
// API calls in parallel on mount, interleaves the results by timestamp,
// and surfaces the top N items with click-through to the right page.
//
// Fetch is permission-aware — if the user can't see a channel the
// widget silently omits it. Fails soft: a channel that errors out is
// skipped, the other two still render.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  LocalHospital as AlarmIcon,
  BugReport as TicketIcon,
  Notifications as NotificationIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { alarmsApi, ticketsApi } from '../../services/apiClient';
import notificationService from '../../services/notificationService';
import { usePermissions } from '../../contexts/PermissionContext';
import SkeletonList from '../../components/SkeletonList';
import EmptyState from '../../components/EmptyState';

const MAX_ITEMS = 8;

// Normalise any of the three sources into a single flat event shape.
const normalizeAlarm = (a) => ({
  kind: 'alarm',
  id: `alarm-${a.id}`,
  title: a.type || 'Alarm',
  subtitle: a.person || a.location?.friendlyName || a.deviceKey || '—',
  timestamp: a.firstPressAt || a.createdAt || a.created_at,
  path: `/alarms/${a.id}`,
  chip: { label: a.state || a.stateCode || 'active', color: 'error' },
});
const normalizeTicket = (t) => ({
  kind: 'ticket',
  id: `ticket-${t.id}`,
  title: t.title || t.subject || `Ticket #${t.id}`,
  subtitle: t.locationName || t.location?.friendlyName || t.description?.slice(0, 72) || '',
  timestamp: t.updatedAt || t.createdAt || t.updated_at || t.created_at,
  path: `/tickets?id=${t.id}`,
  chip: { label: t.status || 'open', color: 'info' },
});
const normalizeNotification = (n) => ({
  kind: 'notification',
  id: `notif-${n.id}`,
  title: n.title || 'Notification',
  subtitle: (n.body || '').slice(0, 120),
  timestamp: n.created_at || n.createdAt,
  path: '/notifications',
  chip: n.priority ? { label: n.priority, color: 'warning' } : null,
});

const iconFor = (kind) => {
  if (kind === 'alarm') return <AlarmIcon color="error" fontSize="small" />;
  if (kind === 'ticket') return <TicketIcon color="info" fontSize="small" />;
  return <NotificationIcon color="warning" fontSize="small" />;
};

const RecentActivityWidget = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);

      const canAlarms = hasPermission ? (hasPermission('alarms.view') || true) : true;
      const canTickets = hasPermission ? hasPermission('tickets.view') : true;

      const channels = [];

      if (canAlarms) {
        channels.push(
          alarmsApi.getAlarmHistory({ limit: 5 })
            .then((res) => (res.data?.data?.alarms || res.data?.data || res.data || []).map(normalizeAlarm))
            .catch(() => []),
        );
      }
      if (canTickets) {
        channels.push(
          // The public sort contract is `sort`/`order` (keys from TICKET_SORTABLE). The old
          // `sortBy` + `order` mix was never a valid pair: `order` belongs to `sort`.
          ticketsApi.getAll({ limit: 5, sort: 'updatedAt', order: 'desc' })
            .then((res) => (res.data?.tickets || res.data?.data || res.data || []).map(normalizeTicket))
            .catch(() => []),
        );
      }
      channels.push(
        notificationService.getNotifications({ limit: 5, includeRead: true })
          .then((res) => (res?.notifications || res?.data?.notifications || []).map(normalizeNotification))
          .catch(() => []),
      );

      try {
        const results = await Promise.all(channels);
        if (cancelled) return;
        const merged = results
          .flat()
          .filter((i) => i && i.timestamp)
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, MAX_ITEMS);
        setItems(merged);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load recent activity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [hasPermission]);

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Recent activity
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        The latest alarms, tickets, and notifications you can see — tap to open.
      </Typography>

      {loading ? (
        <SkeletonList rows={5} variant="cards" />
      ) : error ? (
        <EmptyState
          type="generic"
          title="Couldn't load recent activity"
          description={error}
        />
      ) : items.length === 0 ? (
        <EmptyState
          type="generic"
          title="All quiet"
          description="No alarms, tickets, or notifications in the last window. Enjoy the silence."
        />
      ) : (
        <List disablePadding>
          {items.map((item) => (
            <ListItemButton
              key={item.id}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                alignItems: 'flex-start',
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>{iconFor(item.kind)}</ListItemIcon>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {item.title}
                    </Typography>
                    {item.chip && (
                      <Chip
                        label={item.chip.label}
                        size="small"
                        color={item.chip.color}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.7rem' }}
                      />
                    )}
                  </Stack>
                }
                secondary={
                  <Box>
                    {item.subtitle && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                        noWrap
                      >
                        {item.subtitle}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>
                      {item.timestamp
                        ? formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })
                        : '—'}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default RecentActivityWidget;
